import CancelIcon from '@mui/icons-material/Cancel';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Typography,
    useMediaQuery
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import searchIcon from '../../../../assets/images/search-icon.svg';
import {
    ARCHIVED_STATUS,
    CONVERT_FINISHED_STATUS,
    CONVERT_STATUS,
    CONVERTING_STATUS,
    DRAFT,
    NOT_PUBLISHED,
    UPLOADING_STATUS
} from '../../../../constants';
import {
    CONNECTED_ACCOUNT_LOAD_FINISHED,
    DELETE_DRAFT_EVENT,
    EVENT_LIST_PAGE_LOADED
} from '../../../../constants/actionTypes';
import fetch from '../../../../fetch';
import sanity from '../../../../sanity';
import { fromUnitAmount } from '../../../../utils/unitAmountHelper';
import NavDesktop from '../../../Nav/NavDesktop';
import NavMobile from '../../../Nav/NavMobile';
import styles from './myLibrary.module.scss';
import theme from '../../../../theme';
import LibraryContentRow from './components/LibraryContentRow';

const mapStateToProps = (state) => ({
    events: state.event.events,
    user: state.user.currentUser,
    company: state.common.company,
    upload: state.common.upload,
    isAdmin: state.user.currentUser.groups && state.user.currentUser.groups.includes('Admin'),
    currency: state.common.currency
});

const mapDispatchToProps = (dispatch) => ({
    setConnectedAccount: (payload) => dispatch({ type: CONNECTED_ACCOUNT_LOAD_FINISHED, payload }),
    onLoad: (payload) => dispatch({ type: EVENT_LIST_PAGE_LOADED, payload }),
    onDeleteDraftEvent: (payload) => dispatch({ type: DELETE_DRAFT_EVENT, payload })
});

const MyLibrary = (props) => {
    const { t } = useTranslation();
    const history = useHistory();
    const isMobile = useMediaQuery(theme.breakpoints.down('mobile'));

    const [loading, setLoading] = useState(false);

    const [videos, setVideos] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [prices, setPrices] = useState();
    const [publishingVideos, setPublishingVideos] = useState([]);
    const [refetchVideos, setRefetchVideos] = useState();
    const [publishingListChanged, setPublishingListChanged] = useState(false);
    const [filteredVideos, setFilteredVideos] = useState(videos);

    const getQueryParams = (search) => {
        return new URLSearchParams(search);
    };

    const fetchSubscription = async () => {
        const queryParams = getQueryParams(location.search);
        const session_id = queryParams.get('session_id');
        const account = await fetch.StripeAccount.getAccountByCompany(
            process.env.REACT_APP_COMPANY_ID
        );
        props.setConnectedAccount(account);
        const subscriptionStatus = await fetch.StripeAccount.getSubscriptionStatus(
            props.user?.data?.email ?? '',
            process.env.REACT_APP_COMPANY_ID,
            props.user?.groups?.includes('Admin') ? undefined : account?.accountId,
            session_id ?? ''
        );
        return subscriptionStatus;
    };

    useEffect(() => {
        if (!props.isAdmin) {
            fetchSubscription();
        }
    }, []);

    useEffect(() => {
        const fetchPrices = async () => {
            try {
                setLoading(true);
                const videoIds = videos.map((v) => v.pay_per_view_product);
                const prices = await fetch.Prices.Latest(videoIds, videos.length, undefined);
                setPrices(prices.data);
            } catch (error) {
                console.log('ERROR: ', error);
            } finally {
                setLoading(false);
            }
        };
        if (videos.length > 0) {
            fetchPrices();
        }
    }, [videos]);

    useEffect(() => {
        const query = searchQuery.toLowerCase();
        const results = videos.filter(
            (video) =>
                video.title.toLowerCase().includes(query) ||
                video.description.toLowerCase().includes(query)
        );
        setFilteredVideos(results);
    }, [searchQuery, videos]);

    useEffect(() => {
        const fetchData = async () => {
            let events = props.events;
            const publishingEvents = events
                .filter(
                    (event) =>
                        event.processingStatus === CONVERTING_STATUS ||
                        event.processingStatus === UPLOADING_STATUS ||
                        event.processingStatus === CONVERT_STATUS ||
                        (event.stream_status !== ARCHIVED_STATUS &&
                            event.stream_status !== NOT_PUBLISHED)
                )
                .filter((e) => e.processingStatus !== undefined);
            setPublishingVideos([...publishingEvents]);
            if (publishingListChanged || publishingEvents.length > 0) {
                setRefetchVideos(publishingEvents.length);
                setPublishingListChanged(true);
            }
        };
        if (props.events.length > 0 && props.isAdmin) {
            fetchData();
        }
    }, [props.events, props.isAdmin]);

    useEffect(() => {
        if (props.isAdmin) {
            if (refetchVideos === 0) {
                setRefetchVideos(null);
                setPublishingListChanged(false);
                loadEvents();
            }
        }
    }, [refetchVideos]);

    const loadEvents = async () => {
        let events = await sanity.Events.GetEvents(true);
        let finishedEvents = events
            .filter(
                (event) =>
                    event.stream_status === 'archived' ||
                    (event.stream_status === 'upcoming' &&
                        event.processingStatus === CONVERT_FINISHED_STATUS) ||
                    event.stream_status === 'active'
            )
            .sort((e1, e2) => e2.startDate.getTime() - e1.startDate.getTime());
        if (!props.isAdmin) {
            const subscriptionStatus = await fetchSubscription();
            const filteredEvents = finishedEvents
                .filter((event) => subscriptionStatus.events.includes(event.id))
                .map((event) => {
                    return event;
                });
            finishedEvents = filteredEvents;
        }
        const result = await fetch.StripeAccount.getEarnings(props.company._id);
        const earnings = result.earnings;
        const enrichedEvents = finishedEvents
            .map((event) => {
                const eventEarnings = earnings[event.id];

                if (eventEarnings) {
                    const currencyKey = Object.keys(eventEarnings)[0];
                    const currencyData = eventEarnings[currencyKey];
                    return Object.assign(event, currencyData, {
                        streams: currencyData.count
                    });
                }
                return Object.assign(event, { streams: 0 });
            })
            .sort((e1, e2) => e2.startDate.getTime() - e1.startDate.getTime());
        setVideos(enrichedEvents);
        let filterTempEvents = events.filter((event) => event.stream_status !== DRAFT);
        props.onLoad(filterTempEvents);
    };

    useEffect(() => {
        if (props.company) {
            loadEvents();
        }
    }, [props.company]);

    const getPriceById = useCallback(
        (productId) => {
            const product = prices.find((item) => item.product === productId);
            if (product) {
                if (props.currency.alignment === 'right') {
                    return `${fromUnitAmount(
                        product.unit_amount_decimal
                    )} ${product.currency?.toUpperCase()}`;
                } else {
                    return `${product.currency?.toUpperCase()} ${fromUnitAmount(
                        product.unit_amount_decimal
                    )}`;
                }
            }
            return null;
        },
        [prices, props.currency.alignment]
    );

    const isVideosLoaded = !loading && !!prices;

    return (
        <Box>
            {isMobile && <NavMobile publishingVideos={publishingVideos} />}
            <Box>
                {!isMobile && (
                    <NavDesktop
                        spinnerColor={'black'}
                        badgeColor={'white'}
                        publishingVideos={publishingVideos}
                    />
                )}
                <div className={styles.gridLayout}>
                    <Box className={styles.header}>
                        <Typography
                            fontSize="40px"
                            lineHeight="52px"
                            fontFamily="InstrumentSans-semibold">
                            {t('MyLibrary')}
                        </Typography>
                        {filteredVideos.length > 0 && prices && (
                            <Typography
                                fontSize="40px"
                                lineHeight="52px"
                                fontFamily="InstrumentSans-semibold"
                                sx={{ opacity: 0.6 }}>
                                {filteredVideos.length} links
                            </Typography>
                        )}
                    </Box>
                    <div />

                    <Box className={styles.links}>
                        {!isMobile && (
                            <Typography
                                sx={{
                                    fontSize: '20px',
                                    lineHeight: '100%',
                                    letterSpacing: '-0.4px'
                                }}>
                                {t('AllLinks')}
                            </Typography>
                        )}
                        <Box className={styles.searchAndButton}>
                            <OutlinedInput
                                sx={{
                                    paddingRight: '0',
                                    borderRadius: '12px',
                                    borderColor: 'transparent',
                                    backgroundColor: '#F8F8F8',
                                    '& fieldset': { border: 'none' }
                                }}
                                id="outlined-adornment-amount"
                                placeholder="Search"
                                size="small"
                                value={searchQuery}
                                fullWidth={isMobile}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <img src={searchIcon} />
                                    </InputAdornment>
                                }
                                endAdornment={
                                    searchQuery && (
                                        <IconButton
                                            sx={{ color: '#ccc' }}
                                            onClick={() => {
                                                setSearchQuery('');
                                            }}>
                                            <CancelIcon />
                                        </IconButton>
                                    )
                                }
                            />
                            {props.isAdmin && (
                                <div>
                                    <Button
                                        onClick={() => {
                                            props.onDeleteDraftEvent();
                                            history.push('/home');
                                        }}
                                        className={styles.linkButton}>
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontSize: '24px !important',
                                                marginRight: '8px'
                                            }}>
                                            +
                                        </Typography>
                                        <Typography component="span" fontSize={'16px'}>
                                            {t('CreateLink')}
                                        </Typography>
                                    </Button>
                                </div>
                            )}
                        </Box>
                    </Box>
                    <div />

                    {isVideosLoaded &&
                        filteredVideos.map((video, index) => (
                            <LibraryContentRow
                                key={video.id}
                                video={video}
                                getPriceById={getPriceById}
                                showBorder={index + 1 < filteredVideos.length}
                            />
                        ))}
                </div>

                {!!loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                        <CircularProgress />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MyLibrary);
